import { z } from "zod";
import { DATE_RANGE_WITH_CUSTOM_VALUES } from "@/lib/date-range";
import {
  AUDIT_ACTIONS,
  AUDIT_SOURCES,
  type AuditAction,
  type AuditSource,
  RESOURCE_TYPES,
  type ResourceType,
} from "./utils/enum-helpers";

export const AUDIT_LOGS_DESCRIPTION =
  "Every action recorded in this project, newest first. Track who did what, when, and from where, over a preset window or an exact date range.";

export const AUDIT_LOGS_PAGE_SIZES = [25, 50, 100] as const;

const DEFAULT_PAGE_SIZE = 25;

// biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
const auditActionValues = Object.values(AUDIT_ACTIONS) as [
  AuditAction,
  ...AuditAction[],
];
// biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
const auditSourceValues = Object.values(AUDIT_SOURCES) as [
  AuditSource,
  ...AuditSource[],
];
// biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
const resourceTypeValues = Object.values(RESOURCE_TYPES) as [
  ResourceType,
  ...ResourceType[],
];

export const auditLogsSearchSchema = z.object({
  actions: z.array(z.enum(auditActionValues)).catch([]).default([]),
  sources: z.array(z.enum(auditSourceValues)).catch([]).default([]),
  resourceTypes: z.array(z.enum(resourceTypeValues)).catch([]).default([]),
  dateRange: z.enum(DATE_RANGE_WITH_CUSTOM_VALUES).catch("all").default("all"),
  startTime: z.string().catch("").default(""),
  endTime: z.string().catch("").default(""),
  limit: z
    .number()
    .int()
    .min(1)
    .max(AUDIT_LOGS_PAGE_SIZES.at(-1) ?? 100)
    .catch(DEFAULT_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.number().int().min(0).catch(0).default(0),
  logId: z.string().catch("").default(""),
});

export type AuditLogsSearch = z.infer<typeof auditLogsSearchSchema>;

export const auditLogsSearchDefaults: AuditLogsSearch = {
  actions: [],
  sources: [],
  resourceTypes: [],
  dateRange: "all",
  startTime: "",
  endTime: "",
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
  logId: "",
};

/** The subset of the search state that narrows the result set. */
export type AuditLogsFilters = Omit<
  AuditLogsSearch,
  "limit" | "offset" | "logId"
>;

/**
 * Pagination and the open-panel id must stay out of the filter set: they belong
 * to different query keys, and folding them in would refetch the list every
 * time a row is opened.
 */
export const pickFilters = ({
  actions,
  sources,
  resourceTypes,
  dateRange,
  startTime,
  endTime,
}: AuditLogsSearch): AuditLogsFilters => ({
  actions,
  sources,
  resourceTypes,
  dateRange,
  startTime,
  endTime,
});

export const auditLogsFilterDefaults: AuditLogsFilters = {
  actions: [],
  sources: [],
  resourceTypes: [],
  dateRange: "all",
  startTime: "",
  endTime: "",
};
